// Operator 2026-10-18: "i want each operators name when they get their own
// login details ... So an operator can add their details and Admin can see
// them all". The admin's roster: every account with its name, login, role
// and last sign-in; add (with a temporary password the operator must change
// at first sign-in), rename, change role, remove.
//
// Rebuilt against the backend (api/routes_operators.py): the previous tab
// sent {name, email} to an endpoint that requires display_name and an initial
// password and forbids unknown fields, read `name` where the API returns
// `display_name`, and removed by email where the API removes by id — so
// adding, listing names and removing had never worked from this screen.
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Users } from "@phosphor-icons/react";

import {
  addOperator, removeOperator, setOperatorName, setOperatorRole,
} from "../../lib/authApi";
import { errorMessage } from "../../lib/errorMessage";
import { Section } from "../SettingsPrimitives";

const ROLE_LABEL = { ADMIN: "Admin", OPERATOR: "Operator" };
const INPUT = "bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 px-2 py-1 text-xs rounded-xl text-slate-900 dark:text-slate-50";
const EMPTY = { display_name: "", email: "", initial_password: "", role: "OPERATOR" };

function OperatorRow({ op, onChanged, onRemoved, setError }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(op.display_name || "");
  const run = async (fn) => {
    setError(null);
    try { await fn(); } catch (e) { setError(errorMessage(e)); }
  };
  return (
    <div data-testid={`operator-${op.email}`}
      className="grid grid-cols-12 gap-2 text-xs items-center py-1.5 border-b border-slate-200 dark:border-slate-700">
      <span className="col-span-3 min-w-0">
        {editing ? (
          <input aria-label={t("ui.settings.fields.operator_name", { defaultValue: "Operator name" })} data-testid={`operator-name-input-${op.email}`} value={name}
            onChange={(e) => setName(e.target.value)} className={`${INPUT} w-full`} />
        ) : (
          <span className="text-slate-900 dark:text-slate-50 break-words">{op.display_name}</span>
        )}
      </span>
      <span className="col-span-3 min-w-0 text-slate-600 dark:text-slate-300 break-all">{op.email}</span>
      <span className="col-span-2">
        <select aria-label={t("ui.settings.operators.col_role", { defaultValue: "Role" })} data-testid={`operator-role-${op.email}`} value={op.role}
          onChange={(e) => run(async () => onChanged(await setOperatorRole(op.id, e.target.value)))}
          className={INPUT}>
          <option value="OPERATOR">{ROLE_LABEL.OPERATOR}</option>
          <option value="ADMIN">{ROLE_LABEL.ADMIN}</option>
        </select>
      </span>
      <span className="col-span-2 text-slate-500 dark:text-slate-400 tabular-nums">
        {op.last_login_at ? String(op.last_login_at).slice(0, 10) : t("ui.settings.operators.never_signed_in", { defaultValue: "never signed in" })}
      </span>
      <span className="col-span-2 flex gap-2 justify-end">
        {editing ? (
          <button type="button" data-testid={`operator-name-save-${op.email}`}
            onClick={() => run(async () => { onChanged(await setOperatorName(op.id, name)); setEditing(false); })}
            className="text-teal-700 dark:text-teal-300 font-semibold">{t("ui.settings.operators.save", { defaultValue: "Save" })}</button>
        ) : (
          <button type="button" data-testid={`operator-rename-${op.email}`} onClick={() => setEditing(true)}
            className="text-teal-700 dark:text-teal-300">{t("ui.settings.operators.rename", { defaultValue: "Rename" })}</button>
        )}
        <button type="button" data-testid={`operator-remove-${op.email}`}
          onClick={() => run(async () => { await removeOperator(op.id); onRemoved(op); })}
          className="text-red-700 dark:text-red-300">{t("ui.settings.operators.remove", { defaultValue: "Remove" })}</button>
      </span>
    </div>
  );
}

export function OperatorsTab({ operators, setOperators }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState(null);
  const add = async () => {
    setError(null);
    try {
      const op = await addOperator({ ...form, email: form.email.trim(), display_name: form.display_name.trim() });
      setOperators([...operators, op]);
      setForm(EMPTY);
    } catch (e) { setError(errorMessage(e)); }
  };
  const replace = (op) => setOperators(operators.map((o) => (o.id === op.id ? op : o)));
  const drop = (op) => setOperators(operators.filter((o) => o.id !== op.id));
  return (
    <Section titleKey="ui.settings.sections.operators" icon={<Users size={14} />}>
      <div className="text-xs text-slate-500 dark:text-slate-400">
        {t("ui.settings.operators.explainer", { defaultValue: "Each person signs in with their own login. Their name is what appears on sign-offs, audit records and the client's Excel file. A new operator must change the temporary password at first sign-in, and can change their own name from the name in the top-right corner." })}
      </div>
      {operators.length === 0 ? (
        <div data-testid="no-operators" className="text-xs text-slate-500 dark:text-slate-400">{t("ui.settings.messages.no_operators")}</div>
      ) : (
        <div>
          <div className="grid grid-cols-12 gap-2 text-xs text-slate-500 dark:text-slate-400 pt-2 pb-1 border-b border-slate-200 dark:border-slate-700">
            <span className="col-span-3">{t("ui.settings.operators.col_name", { defaultValue: "Name" })}</span>
            <span className="col-span-3">{t("ui.settings.operators.col_email", { defaultValue: "Login email" })}</span>
            <span className="col-span-2">{t("ui.settings.operators.col_role", { defaultValue: "Role" })}</span>
            <span className="col-span-2">{t("ui.settings.operators.col_last_sign_in", { defaultValue: "Last sign-in" })}</span>
            <span className="col-span-2" />
          </div>
          {operators.map((o) => (
            <OperatorRow key={o.id || o.email} op={o} onChanged={replace} onRemoved={drop} setError={setError} />
          ))}
        </div>
      )}
      <form className="grid grid-cols-12 gap-2 mt-3" onSubmit={(e) => { e.preventDefault(); add(); }}>
        <input data-testid="new-operator-name" aria-label={t("ui.settings.operators.col_name", { defaultValue: "Name" })} placeholder={t("ui.header.my_name.placeholder", { defaultValue: "Full name…" })} autoComplete="off"
          value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} className={`col-span-3 ${INPUT}`} />
        <input data-testid="new-operator-email" aria-label={t("ui.settings.operators.col_email", { defaultValue: "Login email" })} type="email" placeholder="name@company.com…" autoComplete="off" spellCheck={false} // i18n-ignore: example address
          value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={`col-span-3 ${INPUT}`} />
        <input data-testid="new-operator-password" aria-label={t("ui.settings.operators.temp_password", { defaultValue: "Temporary password" })} type="password" placeholder={t("ui.settings.operators.temp_password_placeholder", { defaultValue: "Temporary password (12+)…" })} autoComplete="new-password"
          value={form.initial_password} onChange={(e) => setForm({ ...form, initial_password: e.target.value })} className={`col-span-3 ${INPUT}`} />
        <select data-testid="new-operator-role" aria-label={t("ui.settings.operators.col_role", { defaultValue: "Role" })} value={form.role}
          onChange={(e) => setForm({ ...form, role: e.target.value })} className={`col-span-1 ${INPUT}`}>
          <option value="OPERATOR">{ROLE_LABEL.OPERATOR}</option>
          <option value="ADMIN">{ROLE_LABEL.ADMIN}</option>
        </select>
        <button type="submit" data-testid="add-operator" className="col-span-2 bg-teal-600 text-white font-semibold text-xs rounded-xl">{t("ui.settings.operators.add", { defaultValue: "Add operator" })}</button>
      </form>
      {error && <div data-testid="operator-error" role="alert" className="text-red-700 dark:text-red-300 text-xs">{typeof error === "string" ? error : JSON.stringify(error)}</div>}
    </Section>
  );
}
